import React from 'react';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy: React.FC<{ onBack: () => void }> = ({ onBack }) => (
    <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-sm leading-relaxed text-slate-700">
            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">1. Who we are</h2>
                <p>EstateClarity is operated as a sole-trader digital product. For data protection queries contact <a href="mailto:support@estateclarity.co.uk" className="text-indigo-600 hover:underline">support@estateclarity.co.uk</a>.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">2. What data we collect</h2>
                <p>When you create an account we store your email address and a hashed password. When you use the estate assessment tool we store the information you enter about the estate (asset values, dates, family structure). We do not collect names, addresses, or National Insurance numbers of living individuals.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">3. How we use your data</h2>
                <ul className="list-disc pl-5 space-y-1">
                    <li>To provide the inheritance tax estimate and probate assessment you requested.</li>
                    <li>To save your progress so you can return to your case.</li>
                    <li>To process payments via Stripe (we never see or store card details).</li>
                    <li>To send transactional emails (password resets, receipts).</li>
                </ul>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">4. Legal basis</h2>
                <p>We process your data on the basis of contractual necessity (providing the service you signed up for) and legitimate interest (improving the product). We do not sell your data or use it for marketing without consent.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">5. Data storage and security</h2>
                <p>Data is stored in a PostgreSQL database hosted with encrypted connections. Passwords are hashed using bcrypt. Authentication tokens are stored in HttpOnly, Secure cookies.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">6. Your rights</h2>
                <p>Under UK GDPR you have the right to access, correct, or delete your personal data. You can delete your account and all associated estate data by contacting <a href="mailto:support@estateclarity.co.uk" className="text-indigo-600 hover:underline">support@estateclarity.co.uk</a>. Unclaimed (anonymous) estate data is automatically deleted after 7 days.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">7. Cookies</h2>
                <p>We use a single authentication cookie (<code>auth_token</code>) to keep you signed in. We do not use advertising or tracking cookies. The cookie is HttpOnly and cannot be read by JavaScript.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">8. Third-party services</h2>
                <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Stripe</strong> — payment processing. See <a href="https://stripe.com/privacy" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe's privacy policy</a>.</li>
                    <li><strong>Google Gemini</strong> — AI-powered research assistant. Estate context sent to the AI is anonymised (no names or identifying information).</li>
                </ul>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">9. Changes to this policy</h2>
                <p>We may update this policy from time to time. Material changes will be communicated via email to registered users.</p>
            </section>
        </div>
    </div>
);
